import React, { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Typography,
  DialogContentText,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import './SectionEditorStyle.css';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { env } from '../../../../api/common';
import FileTable from './FileTable';
import { IFileInfo, ISection, loadBalancerAPI } from '../../../../api/load-balancer';
import rehypeRaw from 'rehype-raw';

interface Props {
  onSave: (section: ISection) => void;
  onCancel: () => void;
  section?: ISection | null;
  language?: string;
}

export function SectionEditor({ onSave, onCancel, section, language }: Props) {
  const {
    data: allImagesData,
    isLoading: isLoadingAllImagesData,
    error: errorAllImagesData,
    refetch: refetchAllImagesData,
  } = loadBalancerAPI.hookWrapperQuery.useGetAllImages(language);
  const { data: allMdFilesData } = loadBalancerAPI.hookWrapperQuery.useGetAllMdFiles(language);
  const [sectionName, setSectionName] = useState<string>('quickstart');
  const [hashHyperLink, setHashHyperLink] = useState<string>('#quickstart');

  const [iconURL, setIconURL] = useState<string>('warning-icon.png');
  const [iconName, setIconName] = useState<string>('warning-icon.png');
  const [selectedIcon, setSelectedIcon] = useState<File | null>(null);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);

  const [markdownFile, setMarkdownFile] = useState<string>('');
  const [markdownFileName, setMarkdownFileName] = useState<string>('');
  const [selectedMarkdownFile, setSelectedMarkdownFile] = useState<File | null>(null);
  const [isUploadingMarkdown, setIsUploadingMarkdown] = useState(false);
  const [uploadedMdFiles, setUploadedMdFiles] = useState<IFileInfo[]>([]);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showRawPreview, setShowRawPreview] = useState<boolean>(false);

  const [imageFileName, setImageFileName] = useState<string>('');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState<boolean>(false);
  const [replaceFileType, setReplaceFileType] = useState<string>('');

  const [uploadedImages, setUploadedImages] = useState<IFileInfo[]>([]);
  const [error, setError] = useState<string>('');
  const [modalConfirmDelete, SetmodalConfirmDelete] = useState(false);
  const [fileNameToDelete, setFileNameToDelete] = useState('');
  const [showSizeAlert, setShowSizeAlert] = useState(false);
  const [errorSizeMessage, setErrorSizeMessage] = useState('');

  useEffect(() => {
    if (section) {
      setSectionName(section.sectionName);
      setIconURL(`quickstart/${language}/images/${section.icon}`);
      setMarkdownFileName(section.contentFile);
      fetchMarkdownContent(`quickstart/${language}/md/${section.contentFile}`);
      setIconName(section.icon);
      setHashHyperLink(section.hashHyperLink);
    }
    if (allImagesData) {
      setUploadedImages(allImagesData);
    }
    if (allMdFilesData) {
      setUploadedMdFiles(allMdFilesData);
    }
  }, [section, allImagesData, allMdFilesData, language]);

  const handleSectionNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSectionName = e.target.value;
    setSectionName(newSectionName);
    const newHashHyperLink = `#${newSectionName
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, '_')}`;
    setHashHyperLink(newHashHyperLink);
  };

  const fetchMarkdownContent = async (path: string) => {
    try {
      const response = await fetch(path, {
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      const text = await response.text();
      setMarkdownFile(text);
    } catch (error) {
      console.error('Error fetching markdown file:', error);
    }
  };

  const handleIconUpload = async () => {
    setIsUploadingIcon(true);
    try {
      if (selectedIcon) {
        if (selectedIcon.size > 5 * 1024 * 1024) {
          setErrorSizeMessage('File size exceeds 5MB limit.');
          setShowSizeAlert(true);
          setIsUploadingIcon(false);
          if (section) {
            setIconURL(`quickstart/${language}/images/${section.icon}`);
            setIconName(section.icon);
            setSelectedIcon(null);
          } else {
            setIconURL('warning-icon.png');
            setIconName('warning-icon.png');
            setSelectedIcon(null);
          }
          return;
        }
        const isFileExists = uploadedImages.some((file) => file.filename === selectedIcon.name);
        if (isFileExists) {
          setShowReplaceConfirm(true);
          setReplaceFile(selectedIcon);
          setReplaceFileType('icon');
          setIsUploadingIcon(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', selectedIcon, selectedIcon.name);
        const response = await loadBalancerAPI.apiInstance.uploadImage(formData, language);

        if (response.status) {
          setIconURL(`/quickstart/${language}/images/${selectedIcon.name}`);
          setIconName(iconName);
          const newFile: IFileInfo = {
            filename: iconName,
            createdAt: new Date().toLocaleString(),
          };
          setUploadedImages((prevFiles) => [...prevFiles, newFile]);
          refetchAllImagesData();
        } else {
          setError('Failed to upload icon.');
          throw new Error('Failed to upload icon.');
        }
      } else {
        alert('Please select a file or enter a file path to upload.');
      }
    } catch (error) {
      console.error('Failed to upload icon:', error);
    } finally {
      setIsUploadingIcon(false);
    }
  };

  const handleMarkdownUpload = async () => {
    setIsUploadingMarkdown(true);
    try {
      if (selectedMarkdownFile) {
        if (selectedMarkdownFile.size > 5 * 1024 * 1024) {
          setErrorSizeMessage('File size exceeds 5MB limit.');
          setShowSizeAlert(true);
          setIsUploadingMarkdown(false);
          if (section) {
            setMarkdownFileName(section.contentFile);
            fetchMarkdownContent(`quickstart/${language}/md/${section.contentFile}`);
            setSelectedMarkdownFile(null);
          } else {
            setMarkdownFileName('');
            fetchMarkdownContent('');
            setSelectedMarkdownFile(null);
          }
          return;
        }
        const isFileExists = uploadedMdFiles.some((file) => file.filename === selectedMarkdownFile.name);
        if (isFileExists) {
          setShowReplaceConfirm(true);
          setReplaceFile(selectedMarkdownFile);
          setReplaceFileType('content');
          setIsUploadingMarkdown(false);
          return;
        }
        
        await uploadMarkdownFile(selectedMarkdownFile);
      } else {
        alert('Please select a file or enter a file path to upload.');
      }
    } catch (error) {
      setError('Failed to upload Markdown file.');
      console.error('Failed to upload Markdown file:', error);
    } finally {
      setIsUploadingMarkdown(false);
    }
  };

  const uploadMarkdownFile = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', new Blob([file], { type: 'text/markdown' }), file.name);
  
      const response = await loadBalancerAPI.apiInstance.uploadMdFile(formData, language);
  
      if (response.status) {
        setMarkdownFileName(file.name);
        const newFile: IFileInfo = {
          filename: file.name,
          createdAt: new Date().toLocaleString(),
        };
        setUploadedMdFiles((prevFiles) => [...prevFiles, newFile]);
      } else {
        throw new Error('Failed to upload Markdown file.');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleImageDirectoryUpload = async () => {
    setIsUploadingImage(true);
    try {
      if (selectedImageFile) {
        if (selectedImageFile.size > 5 * 1024 * 1024) {
          setErrorSizeMessage('File size exceeds 5MB limit.');
          setShowSizeAlert(true);
          setIsUploadingImage(false);
          setImageFileName('');
          setSelectedImageFile(null);
          return;
        }
        const isFileExists = uploadedImages.some((file) => file.filename === selectedImageFile.name);
        if (isFileExists) {
          setShowReplaceConfirm(true);
          setReplaceFile(selectedImageFile);
          setReplaceFileType('imageDirectory');
          setIsUploadingImage(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', selectedImageFile);

        const response = await loadBalancerAPI.apiInstance.uploadImage(formData, language);

        if (response.status) {
          setImageFileName(imageFileName);
          const newFile: IFileInfo = {
            filename: imageFileName,
            createdAt: new Date().toLocaleString(),
          };

          setUploadedImages((prevFiles) => [...prevFiles, newFile]);
          refetchAllImagesData();
        } else {
          throw new Error('Failed to upload image directory file.');
        }
      } else {
        alert('Please select a file or enter a file path to upload.');
      }
    } catch (error) {
      setError('Failed to upload image directory file.');
      console.error('Failed to upload image directory file:', error);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedIcon(file);
      setIconURL(URL.createObjectURL(file));
      setIconName(file.name);
    }
  };

  const handleMarkdownFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setMarkdownFileName(file.name);
      setSelectedMarkdownFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setMarkdownFile(reader.result as string);
      };
      reader.readAsText(file);
    }
  };

  const handleImageDirectoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImageFile(file);
      setImageFileName(file.name);
    }
  };

  const handleSave = async () => {
    try {
      if (!sectionName || !markdownFile) {
        setError('Please add section name or MD file');
        return;
      }
      if (sectionName && markdownFile) {
        const newSection: ISection = {
          id: section?.id || Date.now().toString(),
          hashHyperLink,
          sectionName,
          icon: iconName,
          contentFile: markdownFileName,
          isPublish: section?.isPublish || false,
        };
        let response;
        if (section?.id) {
          response = await loadBalancerAPI.apiInstance.editSection(
            section.id,
            iconName,
            sectionName,
            hashHyperLink,
            markdownFileName
          );
        } else {
          response = await loadBalancerAPI.apiInstance.addNewSection(
            iconName,
            sectionName,
            hashHyperLink,
            markdownFileName,
            language
          );
        }

        if (response.status) {
          onSave(newSection);
        } else {
          console.error('Failed to save new section');
        }
      }
    } catch (error) {
      console.error('Failed to save new section:', error);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handlePreview = () => {
    if (!markdownFile) {
      setError('Nothing to preview.');
    } else {
      setShowPreview(true);
    }
  };

  const handleRawPreview = () => {
    if (!markdownFile) {
      setError('Nothing to preview.');
    } else {
      setShowRawPreview(true);
    }
  };

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([markdownFile], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = markdownFileName;
    document.body.appendChild(element);
    element.click();
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setShowRawPreview(false);
  };

  const handleConfirmDeleteImage = (fileName: string) => {
    setFileNameToDelete(fileName);
    SetmodalConfirmDelete(true);
  };

  const handleCloseDeleteConfirm = () => {
    SetmodalConfirmDelete(false);
    setFileNameToDelete('');
  };

  const handleDelete = async () => {
    try {
      const response = await loadBalancerAPI.apiInstance.deleteImage(language, fileNameToDelete);
      if (response.status) {
        setUploadedImages((prevFiles) => prevFiles.filter((file) => file.filename !== fileNameToDelete));
        refetchAllImagesData();
        handleCloseDeleteConfirm();
      } else {
        console.error('Failed to delete section');
      }
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  const handleConfirmReplace = async () => {
    if (replaceFile) {
      if (replaceFileType === 'icon') {
        setSelectedIcon(replaceFile);
        await handleIconUpload();
      } else if (replaceFileType === 'content') {
        const deleteResponse = await loadBalancerAPI.apiInstance.deleteMdFile(language, replaceFile.name);
        if (!deleteResponse.status) {
          throw new Error('Failed to delete existing Markdown file.');
        }
        await uploadMarkdownFile(replaceFile);
      } else if (replaceFileType === 'imageDirectory') {
        setSelectedImageFile(replaceFile);
        await handleImageDirectoryUpload();
      }
    }
    setShowReplaceConfirm(false);
    setReplaceFile(null);
    setReplaceFileType('');
  };

  const handleCancelReplace = () => {
    setShowReplaceConfirm(false);
    setReplaceFile(null);
    setReplaceFileType('');
  };

  const clearError = () => {
    setError('');
  };

  const handleCloseSizeAlert = () => {
    setShowSizeAlert(false);
  };

  return (
    <Card>
      <CardContent sx={{ overflow: 'hidden' }}>
        <div className="button-section-action">
          <button className="item-button cancel-button" onClick={handleCancel}>
            Cancel
          </button>
          <button className="item-button save-button" onClick={handleSave}>
            Save
          </button>
          <button className="item-button preview-button" onClick={handlePreview}>
            Preview
          </button>
          <button className="item-button preview-button" onClick={handleRawPreview}>
            View MD
          </button>
        </div>
        {error && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              alignItems: 'center',
              marginBottom: '10px',
            }}
            className="error-section"
          >
            <span style={{ color: 'red', fontWeight: '600', fontSize: '15px' }}>{error}</span>
            <Button color="error" style={{ fontSize: '15px', color: 'red', fontWeight: '600' }} onClick={clearError}>
              Clear
            </Button>
          </div>
        )}
        <div className="content-section">
          <div className="left-content">
            <div className="quickstart-title item-right-content">
              <div className="label-section">Section:</div>
              <TextField
                className="label-section"
                label="Section name"
                value={sectionName}
                onChange={handleSectionNameChange}
                fullWidth
                margin="normal"
                size="small"
              />
            </div>
            <div className="quickstart-title item-right-content">
              <div className="label-section">Section Hyperlink Reference:</div>
              <span className="label-section">{hashHyperLink}</span>
            </div>
            <div className="icon item-right-content">
              <div className="label-section">Icon:</div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <img src={iconURL} alt={iconName} style={{ width: '30px', height: '30px' }} />
                <span className="label-section">{iconName}</span>
              </div>
            </div>
            <div className="main-content item-right-content">
              <div className="label-section">Content:</div>
              <span className="label-section">{markdownFileName}</span>
            </div>
          </div>
          <div className="right-content">
            <div className="upload-icon item-right-content">
              <div className="label-section">Icon Upload (200x200):</div>
              <div className="responsive-section-input" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="responsive-input-item" style={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    className="label-section"
                    value={iconName}
                    fullWidth
                    placeholder="File size limit to 5mb"
                    variant="outlined"
                    size="small"
                    style={{ flexGrow: 1 }}
                    onChange={(e) => setIconName(e.target.value)}
                  />
                  <input
                    style={{ display: 'none' }}
                    id="upload-icon-file"
                    type="file"
                    accept="image/*"
                    onChange={handleIconChange}
                  />
                  <label htmlFor="upload-icon-file">
                    <button
                      className="item-button button-select"
                      onClick={() => document.getElementById('upload-icon-file')?.click()}
                    >
                      <FolderOpenIcon style={{ marginRight: '5px' }} />
                      Select a File
                    </button>
                  </label>
                </div>
                <Button
                  variant="contained"
                  style={{ backgroundColor: '#a52697', color: '#fff' }}
                  onClick={handleIconUpload}
                >
                  {isUploadingIcon ? (
                    <CircularProgress style={{ width: '25px', height: '25px', margin: '0px 14px' }} />
                  ) : (
                    'Upload'
                  )}
                </Button>
              </div>
            </div>
            <div className="upload-file item-right-content">
              <div className="label-section">MD File Content Upload:</div>
              <div className="responsive-section-input" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="responsive-input-item" style={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    className="label-section"
                    value={markdownFileName}
                    fullWidth
                    placeholder="File size limit to 5mb"
                    variant="outlined"
                    size="small"
                    style={{ flexGrow: 1 }}
                    onChange={(e) => setMarkdownFileName(e.target.value)}
                  />
                  <input
                    style={{ display: 'none' }}
                    id="upload-md-file"
                    type="file"
                    accept=".md"
                    onChange={handleMarkdownFileChange}
                  />
                  <label htmlFor="upload-md-file">
                    <button
                      className="item-button button-select"
                      onClick={() => document.getElementById('upload-md-file')?.click()}
                    >
                      <FolderOpenIcon style={{ marginRight: '5px' }} />
                      Select a File
                    </button>
                  </label>
                </div>
                <Button
                  variant="contained"
                  style={{ backgroundColor: '#a52697', color: '#fff' }}
                  onClick={handleMarkdownUpload}
                >
                  {isUploadingMarkdown ? (
                    <CircularProgress style={{ width: '25px', height: '25px', margin: '0px 14px' }} />
                  ) : (
                    'Upload'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="md-file-upload">
          <div className="image-directory">
            <div className="label-section">Image Directory:</div>
            <div className="responsive-section-input" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="responsive-input-item" style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  className="label-section"
                  value={imageFileName}
                  fullWidth
                  placeholder="File size limit to 5mb"
                  variant="outlined"
                  size="small"
                  style={{ flexGrow: 1 }}
                  onChange={(e) => setImageFileName(e.target.value)}
                />
                <input
                  style={{ display: 'none' }}
                  id="upload-image-directory"
                  type="file"
                  accept="image/*"
                  onChange={handleImageDirectoryChange}
                />
                <label htmlFor="upload-image-directory">
                  <button
                    className="item-button button-select"
                    onClick={() => document.getElementById('upload-image-directory')?.click()}
                  >
                    <FolderOpenIcon style={{ marginRight: '5px' }} />
                    Select a File
                  </button>
                </label>
              </div>
              <Button
                variant="contained"
                style={{ backgroundColor: '#a52697', color: '#fff' }}
                onClick={handleImageDirectoryUpload}
              >
                {isUploadingImage ? (
                  <CircularProgress style={{ width: '25px', height: '25px', margin: '0px 10px' }} />
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </div>
          <div className="directory-path">
            <div className="label-section">Directory path:</div>
            <div className="label-section" style={{ wordBreak: 'break-all' }}>
              {env.siteBaseUrl}/quickstart/{language}/images/
            </div>
          </div>
        </div>
        <div>
          {isLoadingAllImagesData && <CircularProgress />}

          {errorAllImagesData && (
            <Typography variant="h6" color="error">
              Fail to load data
            </Typography>
          )}

          {!isLoadingAllImagesData && !errorAllImagesData && (
            <div className="table-md-files">
              <FileTable uploadedFiles={uploadedImages} onDelete={handleConfirmDeleteImage} />
            </div>
          )}
        </div>
      </CardContent>
      <Dialog open={showPreview} onClose={handleClosePreview} maxWidth="xl" fullWidth>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={iconURL} alt={iconName} style={{ width: '40px', height: '40px' }} />
          <span style={{ fontSize: '20px', fontWeight: '600' }}>{sectionName}</span>
        </DialogTitle>
        <DialogContent>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{markdownFile}</ReactMarkdown>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showReplaceConfirm} onClose={handleCancelReplace}>
        <DialogTitle style={{ fontSize: '18px', fontWeight: '600' }}>File Exists</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: '16px', fontWeight: '500' }}>
            A file with this name already exists. Do you want to replace it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReplace} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmReplace} color="primary">
            Replace
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={modalConfirmDelete} onClose={handleCloseDeleteConfirm}>
        <DialogTitle style={{ fontSize: '18px', fontWeight: '600' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: '16px', fontWeight: '500' }}>
            Are you sure you want to delete this file?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showSizeAlert} onClose={handleCloseSizeAlert}>
        <DialogTitle style={{ fontSize: '18px', fontWeight: '600', color: 'red' }}>File Size Error</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: '16px', fontWeight: '500', color: 'red' }}>{errorSizeMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSizeAlert} color="error">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showRawPreview} onClose={handleClosePreview} maxWidth="xl" fullWidth>
        <DialogTitle className="preview-raw-button">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={iconURL} alt={iconName} style={{ width: '40px', height: '40px' }} />
            <span style={{ fontSize: '20px', fontWeight: '600' }}>{sectionName}</span>
          </div>
          <button className="item-button preview-button" onClick={handleDownload}>
            Download
          </button>
        </DialogTitle>
        <DialogContent>
          <pre>{markdownFile}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default SectionEditor;
